import { useLocation, useParams } from "react-router";

import { AsyncState } from "../../ui/async-state";
import { Badge } from "../../ui/badge";
import { BackLink } from "../../ui/links";
import { Notice } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { useCatalogCapabilities } from "../access";
import { editAccess } from "../allowed-actions";
import { useCatalogCommand } from "../commands";
import { ItemStatusBadge } from "../components/ItemStatusBadge";
import { pillLink } from "../components/styles";
import { describeCatalogError } from "../errors";
import { itemTypeLabels, priceClassLabels } from "../labels";
import { LocationSection } from "../place/LocationSection";
import { PlaceSection } from "../place/PlaceSection";
import { useCatalogItem } from "../queries";
import { useCatalogRuntime } from "../runtime";
import { CommonFieldsSection } from "../sections/CommonFieldsSection";
import { HistoryPanel } from "../sections/HistoryPanel";
import { InternalNotesPanel } from "../sections/InternalNotesPanel";
import { MediaManager } from "../sections/MediaManager";
import { PricingSection } from "../sections/PricingSection";
import { PublishPanel } from "../sections/PublishPanel";
import { ReferencesPanel } from "../sections/ReferencesPanel";
import { SubtypeSection } from "../sections/SubtypeSection";
import type { ItemSectionContext } from "../sections/section-support";
import { TaxonomySection } from "../sections/TaxonomySection";
import { VerificationStamp } from "../sections/VerificationStamp";

const JUMP_LINKS = [
  { id: "publishing", label: "Status and publishing" },
  { id: "details", label: "Details" },
  { id: "place", label: "Place" },
  { id: "location", label: "Location" },
  { id: "type-details", label: "Type details" },
  { id: "pricing", label: "Price" },
  { id: "taxonomy", label: "Categories, tags, people" },
  { id: "media", label: "Images" },
  { id: "verification", label: "Verification" },
  { id: "internal-notes", label: "Internal notes" },
  { id: "history", label: "History" },
] as const;

/** The edit page of one item. Every section saves on its own. */
export function CatalogEditPage() {
  const { itemId = "" } = useParams();
  const location = useLocation();
  const { capabilities, isLoading: rolesLoading } = useCatalogCapabilities();
  const query = useCatalogItem(itemId, capabilities.read);
  const command = useCatalogCommand(itemId);
  const { now } = useCatalogRuntime();

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="The catalog is not available for your role"
        message="Your staff role does not include catalog access."
      />
    );
  }
  if (query.isPending) {
    return (
      <AsyncState kind="loading" title="Loading the item" message="Reading the catalog item." />
    );
  }
  if (query.isError) {
    const failure = describeCatalogError(query.error);
    return (
      <AsyncState
        kind="error"
        title={failure.kind === "not_found" ? "Item not found" : "The item could not be loaded"}
        message={failure.message}
        onRetry={() => void query.refetch()}
        accessory={<BackLink to="/catalog">Back to the catalog</BackLink>}
      />
    );
  }

  const detail = query.data;
  const access = editAccess(detail.item.status, capabilities);
  const ctx: ItemSectionContext = {
    detail,
    readOnly: access.readOnly,
    capabilities,
    command,
    now: now(),
  };
  const created = (location.state as { created?: boolean } | null)?.created === true;

  return (
    <article className="grid min-w-0 gap-6">
      <PageHeader
        title={detail.item.name}
        breadcrumbs={<BackLink to="/catalog">Back to the catalog</BackLink>}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <ItemStatusBadge status={detail.item.status} />
            <Badge tone="outline">{itemTypeLabels[detail.item.itemType]}</Badge>
            <Badge tone="outline">Price: {priceClassLabels[detail.priceClass]}</Badge>
            <Badge tone="outline">Version {detail.item.version}</Badge>
          </span>
        }
      />

      {created ? (
        <Notice tone="success" role="status">
          Draft created. Add the price, categories and images, then submit it for review.
        </Notice>
      ) : null}
      {access.readOnly ? (
        <Notice role="note">
          {access.reason === "archived"
            ? "This item is archived. It cannot be changed."
            : "You have read-only access to the catalog. You can read the item and add internal notes."}
        </Notice>
      ) : null}

      <nav aria-label="Sections of this item">
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {JUMP_LINKS.filter((link) => link.id !== "internal-notes" || capabilities.readNotes).map(
            (link) => (
              <li key={link.id}>
                <a
                  href={`#${link.id}`}
                  className={pillLink}
                  onClick={(event) => {
                    event.preventDefault();
                    document.getElementById(link.id)?.scrollIntoView?.({ block: "start" });
                    document.getElementById(`${link.id}-title`)?.focus?.();
                  }}
                >
                  {link.label}
                </a>
              </li>
            ),
          )}
        </ul>
      </nav>

      <PublishPanel ctx={ctx} />
      <CommonFieldsSection ctx={ctx} />
      <PlaceSection ctx={ctx} />
      <LocationSection ctx={ctx} />
      <SubtypeSection ctx={ctx} />
      <PricingSection ctx={ctx} />
      <TaxonomySection ctx={ctx} />
      <MediaManager ctx={ctx} />
      <VerificationStamp ctx={ctx} />
      <ReferencesPanel ctx={ctx} />
      {capabilities.readNotes ? (
        <InternalNotesPanel itemId={detail.item.id} capabilities={capabilities} />
      ) : null}
      <HistoryPanel itemId={detail.item.id} version={detail.item.version} />
    </article>
  );
}
